/**
 * Chat Messaging Notification Section
 *
 * Displays connected chat messaging integrations (Slack, Teams, GChat, Zulip)
 * in a TanStackTable with Delete actions.
 * Uses persistent header-in-card pattern (Option C) where header is always visible.
 *
 * Structure:
 * - Header Row: Always visible with icon, title, description, and "Browse Integrations" button
 * - Content Row: TanStackTable shown only when integrations exist (conditional)
 *
 * Used in:
 * - NotificationSettingsCards
 */
import {
  ChatBubbleLeftRightIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/solid';
import React, { useMemo } from 'react';

import { TanStackTable } from '@/components/table/TanStackTable';
import { Button } from '@/components/ui/button';
import { SettingContentCard } from '@/sections/settings/components/primitives/SettingContentCard';
import { Account } from '@/types';

import { getChatMessagingColumns } from './columns/chatMessagingColumns';

export interface ChatMessagingSectionProps {
  /** Filtered chat messaging notifications (Slack, Teams, GChat, Zulip) */
  notifications: Account[];
  /** Handler for deleting a chat integration (hidden when undefined) */
  onDelete?: (integration: Account) => void;
  /** Handler for viewing/editing severity (opens ChatMessagingViewModal) */
  onView: (integration: Account) => void;
  /** Handler for adding a chat integration (hidden when undefined) */
  onAdd?: () => void;
  /** Loading status */
  status?: 'error' | 'success' | 'pending';
  /** Error object if status is error */
  error?: Error | null;
}

export const ChatMessagingSection: React.FC<ChatMessagingSectionProps> = ({
  notifications,
  onDelete,
  onView,
  onAdd,
  status = 'success',
  error = null,
}) => {
  // Column definitions with action handlers
  const columns = useMemo(
    () =>
      getChatMessagingColumns({
        onDelete,
        onView,
      }),
    [onDelete, onView]
  );

  return (
    <section
      aria-labelledby="chat-messaging-heading"
      data-testid="chat-messaging-section"
      data-tutorial="notif-chat"
    >
      <SettingContentCard padding="sm">
        <div className="flex flex-col gap-4">
          {/* Header Row - ALWAYS visible */}
          <div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ChatBubbleLeftRightIcon className="size-5 shrink-0 opacity-50" />
                <div className="flex flex-col gap-1">
                  <h4 id="chat-messaging-heading" className="font-bold">
                    Chat Messaging
                  </h4>
                  <span className="text-chariot-text-secondary text-xs">
                    Send alerts to Slack, Microsoft Teams, Google Chat, or Zulip
                  </span>
                </div>
              </div>
              {onAdd && (
                <Button
                  onClick={onAdd}
                  size="sm"
                  data-testid="chat-messaging-browse-button"
                >
                  <PlusCircleIcon className="size-4" />
                  Add Integration
                </Button>
              )}
            </div>
          </div>

          {/* Table Row - Only when data exists */}
          {notifications.length > 0 && (
            <div className="border-chariot-stroke overflow-hidden rounded-lg border">
              <TanStackTable
                name="chat-messaging"
                columns={columns}
                data={notifications}
                status={status}
                error={error}
              />
            </div>
          )}
        </div>
      </SettingContentCard>
    </section>
  );
};
